<script setup lang="ts">
import { type Component, computed, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import {
  HOVER_PLATE_EDGE_GAP,
  HOVER_PLATE_HEIGHT,
  HOVER_PLATE_MOTION_MS,
  HOVER_PLATE_OPEN_DELAY,
  HOVER_PLATE_PAD_X,
} from '@/modules/common/consts/ui.ts';

const {
  variant = 'overlay',
  is = 'button',
  tone = 'inherit',
  disabled = false,
  delay = HOVER_PLATE_OPEN_DELAY,
  href,
} = defineProps<{
  variant?: 'overlay' | 'inline';
  is?: string | Component;
  tone?: 'inherit' | 'link';
  disabled?: boolean;
  delay?: number;
  href?: string;
}>();

const emit = defineEmits<{
  click: [MouseEvent];
}>();

defineSlots<{
  default: () => unknown;
  plate?: () => unknown;
  trailing?: (slotProps: { expanded: boolean }) => unknown;
}>();

const isOverlay = computed(() => variant === 'overlay');
const wrapRef = useTemplateRef<HTMLElement>('wrap');
const plateRef = useTemplateRef<HTMLElement>('plate');
const hovered = ref(false);
const expanded = ref(false);
const plateStyle = ref<Record<string, string>>({});
const plateFontStyle = ref<Record<string, string>>({});
let hoverTimer: number | null = null;
let leaveTimer: number | null = null;
let hideTimer: number | null = null;
let expandRaf = 0;
let positionBound = false;
let unmounted = false;

const plateInlineStyle = computed(() => ({
  ...plateFontStyle.value,
  ...plateStyle.value,
}));

function clearHoverTimer() {
  if (hoverTimer !== null) {
    window.clearTimeout(hoverTimer);
    hoverTimer = null;
  }
}

function clearLeaveTimer() {
  if (leaveTimer !== null) {
    window.clearTimeout(leaveTimer);
    leaveTimer = null;
  }
}

function clearHideTimer() {
  if (hideTimer !== null) {
    window.clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function cancelExpand() {
  if (expandRaf) {
    window.cancelAnimationFrame(expandRaf);
    expandRaf = 0;
  }
}

function capturePlateFont() {
  const el = wrapRef.value;

  if (!el) {
    return;
  }

  const style = getComputedStyle(el);

  plateFontStyle.value = {
    color: style.color,
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    fontStyle: style.fontStyle,
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight,
  };
}

function updatePlateStyle() {
  if (!isOverlay.value) {
    return;
  }

  const el = wrapRef.value;

  if (!el) {
    return;
  }

  const rect = el.getBoundingClientRect();
  const left = rect.left - HOVER_PLATE_PAD_X;
  const top = rect.top + (rect.height - HOVER_PLATE_HEIGHT) / 2;

  plateStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    height: `${HOVER_PLATE_HEIGHT}px`,
    minHeight: `${HOVER_PLATE_HEIGHT}px`,
    maxWidth: `${Math.max(0, window.innerWidth - left - HOVER_PLATE_EDGE_GAP)}px`,
  };
}

function bindPlatePosition() {
  if (!isOverlay.value || positionBound) {
    return;
  }

  positionBound = true;
  window.addEventListener('scroll', updatePlateStyle, true);
  window.addEventListener('resize', updatePlateStyle);
}

function unbindPlatePosition() {
  if (!positionBound) {
    return;
  }

  positionBound = false;
  window.removeEventListener('scroll', updatePlateStyle, true);
  window.removeEventListener('resize', updatePlateStyle);
}

function openPlate() {
  clearHideTimer();
  cancelExpand();
  capturePlateFont();
  updatePlateStyle();
  expanded.value = false;
  hovered.value = true;
  bindPlatePosition();

  if (!isOverlay.value) {
    expanded.value = true;
    return;
  }

  expandRaf = window.requestAnimationFrame(() => {
    expandRaf = window.requestAnimationFrame(() => {
      expandRaf = 0;

      if (!hovered.value || unmounted) {
        return;
      }

      expanded.value = true;
    });
  });
}

function closePlate() {
  cancelExpand();
  expanded.value = false;
  clearHideTimer();

  if (!isOverlay.value) {
    hovered.value = false;
    return;
  }

  hideTimer = window.setTimeout(() => {
    hovered.value = false;
    unbindPlatePosition();
    hideTimer = null;
  }, HOVER_PLATE_MOTION_MS);
}

function onEnter() {
  if (disabled) {
    return;
  }

  clearLeaveTimer();
  clearHideTimer();
  clearHoverTimer();

  if (hovered.value) {
    expanded.value = true;
    updatePlateStyle();
    return;
  }

  if (delay <= 0) {
    openPlate();
    return;
  }

  hoverTimer = window.setTimeout(() => {
    hoverTimer = null;
    openPlate();
  }, delay);
}

function isInsideHover(target: EventTarget | null) {
  return target instanceof Node && (wrapRef.value?.contains(target) || plateRef.value?.contains(target) || false);
}

function onLeave(event: MouseEvent | FocusEvent) {
  if (isInsideHover(event.relatedTarget)) {
    return;
  }

  clearHoverTimer();
  clearLeaveTimer();

  if (!isOverlay.value) {
    closePlate();
    return;
  }

  leaveTimer = window.setTimeout(() => {
    closePlate();
    leaveTimer = null;
  }, 40);
}

function onPlateEnter() {
  if (disabled) {
    return;
  }

  clearLeaveTimer();
  clearHideTimer();
  clearHoverTimer();
  cancelExpand();
  hovered.value = true;
  expanded.value = true;
  updatePlateStyle();
  bindPlatePosition();
}

function onClick(event: MouseEvent) {
  if (disabled) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }

  if (is === 'a' || href) {
    event.preventDefault();
  }

  clearHoverTimer();
  clearLeaveTimer();
  closePlate();

  const target = event.currentTarget;

  if (target instanceof HTMLElement) {
    target.blur();
  }

  emit('click', event);
}

watch(hovered, (value) => {
  if (!value) {
    expanded.value = false;
    unbindPlatePosition();
  }
});

onUnmounted(() => {
  unmounted = true;
  clearHoverTimer();
  clearLeaveTimer();
  clearHideTimer();
  cancelExpand();
  unbindPlatePosition();
});
</script>

<template>
  <component
    :is="isOverlay ? 'div' : is"
    ref="wrap"
    class="ui-hover-plate"
    :class="{
      '_overlay': isOverlay,
      '_inline': !isOverlay,
      '_active': hovered && expanded,
      '_disabled': disabled,
      '_tone-link': tone === 'link',
    }"
    :type="!isOverlay && is === 'button' ? 'button' : undefined"
    :href="!isOverlay ? href : undefined"
    :disabled="!isOverlay && is === 'button' ? disabled : undefined"
    @mouseenter="onEnter"
    @mouseleave="onLeave"
    @focusin="onEnter"
    @focusout="onLeave"
    @click="onClick"
  >
    <div
      v-if="isOverlay"
      class="ui-hover-plate__trigger"
    >
      <slot />
    </div>
    <template v-else>
      <slot />

      <span
        v-if="$slots.trailing"
        class="ui-hover-plate__trailing"
      >
        <slot
          name="trailing"
          :expanded="expanded"
        />
      </span>
    </template>

    <Teleport
      v-if="isOverlay"
      to="body"
    >
      <component
        :is="is"
        v-if="hovered"
        ref="plate"
        class="ui-hover-plate__plate"
        :class="{
          '_expanded': expanded,
          '_disabled': disabled,
          '_tone-link': tone === 'link',
        }"
        :style="plateInlineStyle"
        :type="is === 'button' ? 'button' : undefined"
        :href="href"
        @mouseenter="onPlateEnter"
        @mouseleave="onLeave"
        @click.stop="onClick"
      >
        <span class="ui-hover-plate__label">
          <slot name="plate">
            <slot />
          </slot>
        </span>

        <span
          v-if="$slots.trailing"
          class="ui-hover-plate__trailing"
        >
          <slot
            name="trailing"
            :expanded="expanded"
          />
        </span>
      </component>
    </Teleport>
  </component>
</template>

<style scoped lang="scss">
@mixin trailing-meta {
  :deep(.ui-hover-plate__meta) {
    display: inline-flex;
    max-width: 0;
    overflow: hidden;
    flex-shrink: 0;
    align-items: center;
    align-self: center;
    color: var(--color-gray-500);
    font-size: var(--font-size);
    line-height: var(--line-height);
    opacity: 0;
    white-space: nowrap;
    transition:
      max-width var(--duration-fast) ease-out,
      opacity var(--duration-fast) ease-out;
  }

  :deep(.ui-hover-plate__action) {
    display: block;
    width: 0;
    max-width: 0;
    min-width: 0;
    height: 16px;
    flex-shrink: 0;
    overflow: hidden;
    color: var(--color-gray-500);
    fill: currentcolor;
    opacity: 0;
    transition:
      width var(--duration-fast) ease-out,
      max-width var(--duration-fast) ease-out,
      opacity var(--duration-fast) ease-out;
  }
}

.ui-hover-plate {
  min-width: 0;
  max-width: 100%;

  @include trailing-meta;

  &._disabled {
    pointer-events: none;
  }

  &._tone-link {
    color: var(--color-blue-700);

    &._disabled {
      color: var(--color-gray-400);
    }
  }
}

.ui-hover-plate__trigger {
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: center;
}

.ui-hover-plate._inline {
  display: inline-flex;
  box-sizing: border-box;
  max-width: 100%;
  height: 20px;
  min-height: 20px;
  flex-shrink: 1;
  align-items: center;
  gap: 0;
  padding: 2px 0 2px 4px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  vertical-align: top;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  transition:
    background-color var(--duration-fast) ease-out,
    box-shadow var(--duration-fast) ease-out,
    gap var(--duration-fast) ease-out,
    padding var(--duration-fast) ease-out;

  &:not(._active) {
    .ui-hover-plate__trailing {
      width: 0;
      min-width: 0;
      max-width: 0;
      overflow: hidden;
    }
  }

  &._active {
    gap: 8px;
    padding: 2px 4px;
    background: var(--color-white);
    box-shadow: 0 0 8px 0 var(--color-gray-300);

    :deep(.ui-hover-plate__action) {
      width: 16px;
      max-width: 16px;
      opacity: 1;
    }
  }
}

.ui-hover-plate._overlay {
  cursor: pointer;
}

.ui-hover-plate__plate {
  position: fixed;
  z-index: var(--z-index-modal-tooltip);
  display: inline-flex;
  box-sizing: border-box;
  max-width: 100%;
  height: 20px;
  min-height: 20px;
  max-height: 20px;
  align-items: center;
  gap: 0;
  padding: 2px 4px;
  border: 0;
  border-radius: 4px;
  color: inherit;
  background: var(--color-white);
  box-shadow: 0 0 0 0 transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: none;
  transition:
    gap var(--duration-fast) ease-out,
    box-shadow var(--duration-fast) ease-out;

  &._tone-link {
    color: var(--color-blue-700);
  }

  &._disabled {
    pointer-events: none;
    color: var(--color-gray-400);
  }

  @include trailing-meta;

  &._expanded {
    gap: 8px;
    box-shadow: 0 0 8px 0 var(--color-gray-300);

    :deep(.ui-hover-plate__meta) {
      max-width: 6rem;
      opacity: 1;
    }

    :deep(.ui-hover-plate__action) {
      width: 16px;
      max-width: 16px;
      opacity: 1;
    }
  }
}

.ui-hover-plate__label {
  display: flex;
  min-width: 0;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ui-hover-plate__trailing {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: inherit;
}
</style>
